<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} SNMP Job</h1>
    </div>

    <!-- Page-level Error Banner -->
    <div v-if="pageError" class="bg-red-50 dark:bg-red-900/20 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4 flex items-center">
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span class="font-medium">{{ pageError }}</span>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <JobDescriptionInput v-model="formData.job_description" :error="fieldErrors.job_description" :job-name="jobName" />

      <!-- SNMP Version -->
      <div class="flex flex-col">
        <label class="mb-1 font-medium">
          SNMP Version<span class="text-red-500">*</span>
        </label>
        <div class="flex space-x-4">
          <label class="inline-flex items-center">
            <input type="radio" v-model="formData.version" :value="2" class="form-radio h-4 w-4 text-blue-600" />
            <span class="ml-2">v2c</span>
          </label>
          <label class="inline-flex items-center">
            <input type="radio" v-model="formData.version" :value="3" class="form-radio h-4 w-4 text-blue-600" />
            <span class="ml-2">v3</span>
          </label>
        </div>
      </div>

      <!-- v2c Fields -->
      <div v-if="formData.version === 2" class="border-l-4 border-blue-500 pl-4 space-y-4">
        <h3 class="font-medium text-gray-700 dark:text-gray-300">SNMP V2C Settings</h3>
        <div class="flex flex-col">
          <label for="community" class="mb-1 font-medium">
            Community String<span class="text-red-500">*</span>
          </label>
          <input
            id="community"
            v-model="formData.community"
            type="text"
            placeholder="e.g., public"
            :class="inputClass('community')"
          />
          <FieldError :message="fieldErrors.community" />
        </div>
      </div>

      <!-- v3 Fields -->
      <div v-if="formData.version === 3" class="border-l-4 border-green-500 pl-4 space-y-4">
        <h3 class="font-medium text-gray-700 dark:text-gray-300">SNMPv3 Settings</h3>

        <div class="flex flex-col">
          <label for="username" class="mb-1 font-medium">
            Username<span class="text-red-500">*</span>
          </label>
          <input
            id="username"
            v-model="formData.username"
            type="text"
            placeholder="e.g., snmp_user"
            :class="inputClass('username')"
          />
          <FieldError :message="fieldErrors.username" />
        </div>

        <div class="flex flex-col">
          <label for="security_level" class="mb-1 font-medium">
            Security Level<span class="text-red-500">*</span>
          </label>
          <GlassSelect
            v-model="formData.security_level"
            :options="['noAuthNoPriv', 'authNoPriv', 'authPriv']"
            placeholder="Select security level"
            :error="!!fieldErrors.security_level"
          />
          <FieldError :message="fieldErrors.security_level" />
        </div>

        <!-- Auth fields (required for authNoPriv and authPriv) -->
        <template v-if="formData.security_level !== 'noAuthNoPriv'">
          <div class="flex flex-col">
            <label for="password" class="mb-1 font-medium">
              Authentication Password<span class="text-red-500">*</span>
            </label>
            <input
              id="password"
              v-model="formData.password"
              type="password"
              placeholder="SNMPv3 auth passphrase"
              :class="inputClass('password')"
            />
            <FieldError :message="fieldErrors.password" />
          </div>

          <div class="flex flex-col">
            <label for="auth_protocol" class="mb-1 font-medium">
              Auth Protocol<span class="text-red-500">*</span>
            </label>
            <GlassSelect
              v-model="formData.auth_protocol"
              :options="['MD5', 'SHA']"
              placeholder="Select auth protocol"
              :error="!!fieldErrors.auth_protocol"
            />
            <FieldError :message="fieldErrors.auth_protocol" />
          </div>
        </template>

        <!-- Privacy fields (required for authPriv) -->
        <template v-if="formData.security_level === 'authPriv'">
          <div class="flex flex-col">
            <label for="priv_protocol" class="mb-1 font-medium">
              Privacy Protocol<span class="text-red-500">*</span>
            </label>
            <GlassSelect
              v-model="formData.priv_protocol"
              :options="['DES', 'AES']"
              placeholder="Select privacy protocol"
              :error="!!fieldErrors.priv_protocol"
            />
            <FieldError :message="fieldErrors.priv_protocol" />
          </div>

          <div class="flex flex-col">
            <label for="priv_password" class="mb-1 font-medium">
              Privacy Password<span class="text-red-500">*</span>
            </label>
            <input
              id="priv_password"
              v-model="formData.priv_password"
              type="password"
              placeholder="SNMPv3 privacy passphrase"
              :class="inputClass('priv_password')"
            />
            <FieldError :message="fieldErrors.priv_password" />
          </div>
        </template>
      </div>

      <!-- Targets -->
      <div class="flex flex-col">
        <label for="targets" class="mb-1 font-medium">
          Targets (IP or FQDN)<span class="text-red-500">*</span>
        </label>
        <input
          id="targets"
          v-model="targetInput"
          type="text"
          placeholder="Enter IP address or FQDN and press Add"
          :class="inputClass('targets')"
        />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Note: FQDN targets require the probe to have DNS resolution for the hostname.
        </p>
        <FieldError :message="fieldErrors.targets" />
        <div class="flex flex-wrap gap-2 mt-2">
          <span
            v-for="target in targets"
            :key="target"
            class="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded text-sm flex items-center"
          >
            {{ target }}
            <button type="button" @click="removeTarget(target)" class="ml-2 text-red-500 hover:text-red-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </span>
        </div>
        <button
          type="button"
          @click="addTarget"
          :disabled="!targetInput"
          :class="[
            'mt-2 px-3 py-1 rounded text-sm w-fit',
            targetInput ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          ]"
        >
          Add Target
        </button>
      </div>

      <!-- SNMP Metric Profiles (Multi-select from database) -->
      <div class="flex flex-col">
        <label class="mb-1 font-medium">
          SNMP Metric Profiles<span class="text-red-500">*</span>
        </label>
        <p class="text-sm text-gray-500 mb-2">
          Select one or more profiles to define which OIDs to collect. The first selected profile determines the job module name.
          <RouterLink to="/snmp-profiles" class="text-blue-600 hover:underline">Manage profiles</RouterLink>
        </p>
        <FieldError :message="fieldErrors.snmp_metric_profiles" />

        <!-- Loading state -->
        <div v-if="loadingProfiles" class="text-gray-500 text-sm py-2">
          Loading profiles...
        </div>

        <!-- No profiles available -->
        <div v-else-if="availableProfiles.length === 0" class="text-gray-500 text-sm py-2 bg-gray-50 dark:bg-gray-700 rounded px-3">
          No SNMP metric profiles available.
          <RouterLink to="/snmp-profiles/add" class="text-blue-600 hover:underline">Create one</RouterLink>
        </div>

        <!-- Profile selection -->
        <div v-else class="space-y-2 border border-gray-300 dark:border-gray-600 rounded p-3 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-700">
          <label
            v-for="profile in availableProfiles"
            :key="profile.id"
            class="flex items-start space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded"
          >
            <input
              type="checkbox"
              :value="profile.id"
              v-model="selectedProfileIds"
              class="form-checkbox h-5 w-5 text-purple-600 mt-0.5"
            />
            <div class="flex-1">
              <span class="font-medium text-gray-800 dark:text-gray-200">{{ profile.name }}</span>
              <p v-if="profile.description" class="text-xs text-gray-500 dark:text-gray-400">
                {{ profile.description }}
              </p>
              <p class="text-xs text-gray-400 mt-1">
                {{ profile.metrics_count || 0 }} metrics, {{ profile.walks_count || 0 }} walks
              </p>
            </div>
          </label>
        </div>

        <!-- Selected count -->
        <p v-if="selectedProfileIds.length > 0" class="text-sm text-purple-600 dark:text-purple-400 mt-2">
          {{ selectedProfileIds.length }} profile(s) selected
        </p>
      </div>

      <!-- Scrape Settings -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col">
          <label for="scrape_interval" class="mb-1 font-medium">Scrape Interval (seconds)</label>
          <input
            id="scrape_interval"
            v-model.number="formData.scrape_interval"
            type="number"
            min="10"
            placeholder="60"
            class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div class="flex flex-col">
          <label for="scrape_timeout" class="mb-1 font-medium">Scrape Timeout (seconds)</label>
          <input
            id="scrape_timeout"
            v-model.number="formData.scrape_timeout"
            type="number"
            min="5"
            placeholder="30"
            class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          @click="goBack"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {{ isEditMode ? 'Update' : 'Create' }} SNMP Job
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createAlloySNMP, getAlloySNMPById, updateAlloySNMP } from "@/services/alloyService";
import { getAllSNMPProfiles } from "@/services/snmpProfileService";
import FieldError from "@/components/FieldError.vue";
import JobDescriptionInput from "@/components/JobDescriptionInput.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { RouterLink } from "vue-router";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddAlloySNMP",
  components: { FieldError, JobDescriptionInput, PageError, GlassSelect, RouterLink },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      pageError: null,
      jobName: "",
      formData: {
        job_description: "",
        version: 2,
        community: "",
        username: "",
        security_level: "authPriv",
        password: "",
        auth_protocol: "SHA",
        priv_protocol: "AES",
        priv_password: "",
        scrape_interval: 60,
        scrape_timeout: 30,
      },
      targets: [],
      targetInput: "",
      // Database-backed SNMP Metric Profiles
      availableProfiles: [],
      selectedProfileIds: [],
      loadingProfiles: false,
      canAdd: false,
      isLoading: false,
    };
  },
  computed: {
    currentSchema() { return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null; },
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    configId() {
      return this.$route.params.configId;
    },
    jobId() {
      return this.$route.params.jobId;
    },
    isEditMode() {
      return !!this.jobId;
    },
    autoGeneratedJobName() {
      if (this.isEditMode) {
        return "Auto-generated based on entity";
      }
      return "Will be generated after creation";
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    await this.fetchAvailableProfiles();
    if (this.isEditMode) {
      await this.loadExistingJob();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add Alloy SNMP Job");
      }
    },
    async fetchAvailableProfiles() {
      this.loadingProfiles = true;
      try {
        const profiles = await getAllSNMPProfiles(this.currentSchema);
        this.availableProfiles = profiles;
      } catch (error) {
        console.error("Error fetching SNMP profiles:", error);
        this.availableProfiles = [];
      } finally {
        this.loadingProfiles = false;
      }
    },
    async loadExistingJob() {
      this.isLoading = true;
      try {
        const job = await getAlloySNMPById(this.jobId, this.currentSchema);
        // Populate form data
        this.jobName = job.job_name;
        this.formData.job_description = job.job_description || "";
        this.formData.version = job.version;
        this.formData.community = job.community || "";
        this.formData.username = job.username || "";
        this.formData.security_level = job.security_level || "authPriv";
        this.formData.password = job.password || "";
        this.formData.auth_protocol = job.auth_protocol || "SHA";
        this.formData.priv_protocol = job.priv_protocol || "AES";
        this.formData.priv_password = job.priv_password || "";
        this.formData.scrape_interval = job.scrape_interval || 60;
        this.formData.scrape_timeout = job.scrape_timeout || 30;
        this.targets = job.targets || [];
        // Handle database-backed SNMP metric profiles
        // The API returns snmp_metric_profiles as array of IDs and snmp_metric_profiles_detail as objects
        if (job.snmp_metric_profiles && job.snmp_metric_profiles.length > 0) {
          this.selectedProfileIds = job.snmp_metric_profiles;
        } else if (job.snmp_metric_profiles_detail && job.snmp_metric_profiles_detail.length > 0) {
          this.selectedProfileIds = job.snmp_metric_profiles_detail.map(p => p.id);
        }
      } catch (error) {
        console.error("Error loading SNMP job:", error);
        alert("Failed to load job data");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    inputClass(field) {
      return [
        'border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600',
        this.fieldErrors[field] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:ring-blue-500'
      ];
    },
    addTarget() {
      if (this.targetInput && !this.targets.includes(this.targetInput)) {
        this.targets.push(this.targetInput);
        this.targetInput = "";
        this.fieldErrors.targets = null;
      }
    },
    removeTarget(target) {
      this.targets = this.targets.filter((t) => t !== target);
    },
    goBack() {
      this.$router.push({ name: "AlloyDetails", params: { id: this.configId }, query: { schemaName: this.currentSchema, tab: "snmp" } });
    },
    async submitForm() {
      this.pageError = null;
      this.fieldErrors = {};

      if (this.targets.length === 0) {
        this.fieldErrors.targets = "At least one target is required";
        return;
      }

      if (this.selectedProfileIds.length === 0) {
        this.fieldErrors.snmp_metric_profiles = "At least one SNMP Metric Profile is required";
        return;
      }

      // v3 requires an explicit security_level. snmp_exporter rejects empty
      // strings ("must be one of authPriv/authNoPriv/noAuthNoPriv"), so block
      // submission here rather than letting alloy fail at runtime.
      if (this.formData.version === 3) {
        const validLevels = ["noAuthNoPriv", "authNoPriv", "authPriv"];
        if (!validLevels.includes(this.formData.security_level)) {
          this.fieldErrors.security_level = "Select a security level (noAuthNoPriv, authNoPriv, or authPriv)";
          return;
        }
      }

      const data = {
        alloy_config: parseInt(this.configId),
        job_description: this.formData.job_description.trim(),
        version: this.formData.version,
        targets: this.targets,
        scrape_interval: this.formData.scrape_interval,
        scrape_timeout: this.formData.scrape_timeout,
        snmp_metric_profiles: this.selectedProfileIds,
      };

      // v2c specific
      if (this.formData.version === 2) {
        data.community = this.formData.community;
      }

      // v3 specific
      if (this.formData.version === 3) {
        data.username = this.formData.username;
        data.security_level = this.formData.security_level;

        if (this.formData.security_level !== "noAuthNoPriv") {
          data.password = this.formData.password;
          data.auth_protocol = this.formData.auth_protocol;
        }

        if (this.formData.security_level === "authPriv") {
          data.priv_protocol = this.formData.priv_protocol;
          data.priv_password = this.formData.priv_password;
        }
      }

      try {
        if (this.isEditMode) {
          await updateAlloySNMP(this.jobId, data, this.currentSchema);
        } else {
          await createAlloySNMP(data, this.currentSchema);
        }
        this.goBack();
      } catch (error) {
        this.pageError = null;
        this.fieldErrors = {};

        const errData = error?.response?.data;
        if (errData) {
          // Handle field-specific errors
          Object.keys(errData).forEach((key) => {
            const errorMsg = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
            this.fieldErrors[key] = errorMsg;
          });

          // Show page-level error for non-field errors or alloy_config errors
          // Replace technical backend error messages with user-friendly versions
          if (errData.alloy_config || errData.non_field_errors) {
            let errorMessage = errData.alloy_config?.[0] || errData.alloy_config ||
                               errData.non_field_errors?.[0] || errData.non_field_errors;

            // Replace technical error with client-friendly message
            if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('mimir')) {
              errorMessage = "Your organization tenant is not configured. Please contact Splitpoint support for assistance.";
            }

            this.pageError = errorMessage || "Please correct the errors below";
          } else if (Object.keys(errData).length > 0) {
            this.pageError = "Please correct the errors below";
          }
        } else {
          // Generic error message for network/server errors
          this.pageError = `Failed to ${this.isEditMode ? 'update' : 'create'} job. Please try again.`;
        }

        console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} SNMP job:`, error);
      }
    },
  },
};
</script>
